import { Field, Form, Formik } from 'formik';
import { HTTPError } from 'ky';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { FormComponentBaseProps } from '@tmh-website/components/blocks/Form';
import { cmsWebClient } from '@tmh-website/services/cms.client.service';
import { StaticFormPayload } from '@tmh-website/services/form.service';
import Yup from '@tmh-website/services/validation.service';
import { Breakpoint } from '@tmh-website/shared/const';
import { getCaptchaHlBasedOnLocale } from '@tmh-website/shared/helpers';
import Button from '@tmh-website/ui/Button';
import { StyledFormElements } from '@tmh-website/ui/FormElements';
import { ErrorMessage, SuccessMessage } from '@tmh-website/ui/FormResultMessages';
import FormSection from '@tmh-website/ui/FormSection';
import Input from '@tmh-website/ui/Input';
import Select from '@tmh-website/ui/Select';
import Textarea from '@tmh-website/ui/Textarea';

import * as StyledStaticForms from './StaticForms.styles';

export const ContactFormValidation = Yup.object({
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  company: Yup.string(),
  /**
   * This field is not called "email" since that name would auto-trigger an email being sent to the customer upon form submission.
   * For this form, we don't want such an email, thus this field is called different than in the other forms.
   */
  email_address: Yup.string().required(),
  phone: Yup.string(),
  message: Yup.string().required(),
  subject: Yup.string().required(),
  gRecaptchaResponse: Yup.string().required(),
}).strict();

export type Values = Yup.InferType<typeof ContactFormValidation>;

export const ContactFormENLabel: Record<keyof Values, string> = {
  firstName: 'global:label.firstName',
  lastName: 'global:label.lastName',
  company: 'global:label.company',
  email_address: 'global:label.email',
  phone: 'global:label.phone',
  message: 'global:label.yourMessage',
  subject: 'global:label.subject',
  gRecaptchaResponse: '',
};

const ContactFormEN = ({
  formParameters,
  formElementThemeColor,
  buttonThemeColor,
  buttonHoverThemeColor,
  locale,
}: FormComponentBaseProps) => {
  const { t } = useTranslation();

  const {
    mutateAsync: submitMutation,
    isSuccess,
    isError,
  } = useMutation<void, HTTPError, Values>(values => {
    const payload: StaticFormPayload = {
      formParameters,
      locale: locale,
      pagePath: window.location.pathname,
      gRecaptchaResponse: values.gRecaptchaResponse,
      fields: Object.keys(values)
        .filter(key => key !== 'gRecaptchaResponse')
        .map(name => ({ name, label: t(ContactFormENLabel[name]), value: values[name] })),
    };
    return cmsWebClient.post('static-form/submit', { json: payload }).json();
  });

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        subject: '',
        company: '',
        email_address: '',
        phone: '',
        message: '',
        gRecaptchaResponse: '',
      }}
      validationSchema={ContactFormValidation}
      onSubmit={values => submitMutation(values)}>
      {({ isSubmitting, setFieldValue, errors, touched }) => (
        <Form data-cy='contactFormEN'>
          <>
            <FormSection numberOfColumns={{ [Breakpoint.Sm]: 2 }}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ContactFormENLabel.firstName)}
                name='firstName'
                autoComplete='given-name'
                required
              />
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ContactFormENLabel.lastName)}
                name='lastName'
                autoComplete='family-name'
                required
              />
            </FormSection>
            <FormSection numberOfColumns={2}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ContactFormENLabel.company)}
                name='company'
                autoComplete='organization'
              />
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ContactFormENLabel.email_address)}
                name='email_address'
                autoComplete='email'
                type='email'
                required
              />
            </FormSection>
            <FormSection numberOfColumns={1}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ContactFormENLabel.phone)}
                name='phone'
                autoComplete='phone'
                type='tel'
              />
            </FormSection>
            <FormSection numberOfColumns={1}>
              <Field
                as={Select}
                themeColor={formElementThemeColor}
                label={t(ContactFormENLabel.subject)}
                name='subject'
                options={[
                  {
                    label: 'Questions about charging an electric car',
                    value: 'Questions about charging an electric car',
                  },
                  {
                    label: 'Questions about products',
                    value: 'Questions about products',
                  },
                  {
                    label: 'Questions about fleet',
                    value: 'Questions about fleet',
                  },
                  {
                    label: 'Questions about  load management',
                    value: 'Questions about  load management',
                  },
                  {
                    label: 'Questions about incentive programs',
                    value: 'Questions about incentive programs',
                  },
                  {
                    label: 'Questions about energy storage systems',
                    value: 'Questions about energy storage systems',
                  },
                  {
                    label: 'Cooperating with The Mobility House',
                    value: 'Cooperating with The Mobility House',
                  },
                  {
                    label: 'Questions about our company',
                    value: 'Questions about our company',
                  },
                  {
                    label: 'Suggestions for ChargePilot / Charging and Energy Management',
                    value: 'Suggestions for ChargePilot / Charging and Energy Management',
                  },
                  {
                    label: 'Media/press inquiries',
                    value: 'Media/press inquiries',
                  },
                  {
                    label: 'Others',
                    value: 'Others',
                  },
                ]}
                required
              />
            </FormSection>
            <FormSection numberOfColumns={1}>
              <Field
                as={Textarea}
                themeColor={formElementThemeColor}
                label={t(ContactFormENLabel.message)}
                name='message'
                rows='4'
                required
              />
            </FormSection>
            <StyledStaticForms.CaptchaContainer>
              <ReCAPTCHA
                hl={getCaptchaHlBasedOnLocale(locale)}
                onChange={token => setFieldValue('gRecaptchaResponse', token)}
                sitekey={
                  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? process.env.NX_NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ''
                }
                size='normal'
              />
              {touched.gRecaptchaResponse && errors.gRecaptchaResponse && (
                <StyledFormElements.ErrorMessage>{t('global:errorMissingCaptcha')}</StyledFormElements.ErrorMessage>
              )}
            </StyledStaticForms.CaptchaContainer>
            {!isSuccess && (
              <Button
                type='submit'
                themeColor={buttonThemeColor}
                hoverThemeColor={buttonHoverThemeColor}
                isLoading={isSubmitting}
                disabled={isSuccess || Object.keys(touched).length === 0 || Object.keys(errors).length > 0}
                minWidth>
                {t('global:send')}
              </Button>
            )}
            {isSuccess && <SuccessMessage marginTop>{t('global:contact.thanksMessage')}</SuccessMessage>}
            {isError && <ErrorMessage marginTop>{t('global:errorUnknown')}</ErrorMessage>}
          </>
        </Form>
      )}
    </Formik>
  );
};
export default ContactFormEN;
